/**
 * ─────────────────────────────────────────────────────────────
 * SITE CONFIG — static, developer-owned settings.
 *
 * This is the compile-time layer: brand, contact, currency, and
 * *default* opening hours / services. Things the business owner needs
 * to change without a redeploy live in the editable content layer
 * instead (see config/content.registry.ts + lib/content.ts).
 *
 * Rule of thumb:
 *   - changes rarely, set by the developer  → here
 *   - changes often, edited by the owner     → content collection
 * ─────────────────────────────────────────────────────────────
 */
import type { OpeningHour } from '@/lib/types';

export interface SiteConfig {
  name: string;
  legalName: string;
  tagline: string;
  description: string;
  /** Absolute URL of the deployed site (overridden by NEXT_PUBLIC_SITE_URL). */
  url: string;
  locale: string;

  contact: {
    email: string;
    phone: string;
    /** Phone formatted for a tel: link (digits only, intl prefix). */
    phoneHref: string;
    address: string;
    city: string;
    postalCode: string;
    country: string;
    geo?: { lat: number; lng: number };
  };

  social: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };

  /** Brand palette — fed straight into the MUI theme. */
  brand: {
    primary: string;
    secondary: string;
    mode: 'light' | 'dark';
  };

  /** Default opening hours. The owner can override these live via the
   *  `hours` content slot; use resolveHours() to read the effective value. */
  openingHours: OpeningHour[];

  /** Appointment booking defaults. */
  booking: {
    slotMinutes: number;
    horizonDays: number;
    services: string[];
  };

  /** Currency for product prices (ISO 4217). */
  currency: string;
}

export const siteConfig: SiteConfig = {
  name: 'Coiffeur La Fabrique',
  legalName: 'La Fabrique',
  tagline: 'Coiffeur styliste homme & femme, coloriste — quartier Championnet, Grenoble',
  description:
    'La Fabrique, salon de coiffure à Grenoble : coupe, brushing, couleur, balayage, lissage et coiffage par Magali. Quartier Championnet. Rendez-vous par téléphone au 04 76 26 29 07.',
  url: process.env.NEXT_PUBLIC_SITE_URL || 'https://www.example.fr',
  locale: 'fr_FR',

  contact: {
    email: '',
    phone: '04 76 26 29 07',
    phoneHref: '+33476262907',
    address: '4 Rue de Turenne',
    city: 'Grenoble',
    postalCode: '38000',
    country: 'FR',
    geo: { lat: 45.1851638, lng: 5.7217952 },
  },

  social: {
    facebook: 'https://www.facebook.com/p/Salon-de-coiffure-Mag-La-Fabrique-100027924685221/',
    instagram: 'https://www.instagram.com/salonlafabrique.grenoble/',
  },

  // « La Fabrique » → un atelier de coiffure artisanal. Palette chaleureuse:
  // espresso profond pour l'ancrage, cuivre pour l'accent, fond crème teinté.
  brand: {
    primary: '#3a2a1f',
    secondary: '#b5622e',
    mode: 'light',
  },

  // Horaires réels du salon (lundi et dimanche fermés). Source : PagesJaunes
  // et Mappy. Le propriétaire peut les ajuster sans redéployer via le slot
  // « hours », qui devient alors la seule source de vérité (resolveHours).
  openingHours: [
    { day: 2, open: '09:00', close: '19:00' }, // mardi
    { day: 3, open: '09:00', close: '13:00' }, // mercredi
    { day: 4, open: '09:00', close: '19:00' }, // jeudi
    { day: 5, open: '09:00', close: '19:00' }, // vendredi
    { day: 6, open: '09:00', close: '17:00' }, // samedi
  ],

  booking: {
    slotMinutes: 30,
    horizonDays: 30,
    services: ['Coupe', 'Couleur & balayage', 'Brushing', 'Coiffage occasion'],
  },

  currency: 'EUR',
};

export default siteConfig;
